import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, X, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAdmin } from '../context/AdminContext';
import { API_URL } from '../config';



const WHATSAPP_NUMBER = '77472755088';

const priceFormTexts = {
  ru: { title: 'Запросить прайс-лист', name: 'Ваше имя', phone: 'Телефон', city: 'Город', send: 'Отправить', sending: 'Отправка...', success: 'Запрос отправлен!', btn: 'Запросить прайс-лист' },
  kz: { title: 'Прайс-парақ сұрау', name: 'Атыңыз', phone: 'Телефон', city: 'Қала', send: 'Жіберу', sending: 'Жіберілуде...', success: 'Сұрау жіберілді!', btn: 'Прайс-парақ сұрау' },
  en: { title: 'Request Price List', name: 'Your name', phone: 'Phone', city: 'City', send: 'Send', sending: 'Sending...', success: 'Request sent!', btn: 'Request Price List' },
  ar: { title: 'طلب قائمة الأسعار', name: 'اسمك', phone: 'الهاتف', city: 'المدينة', send: 'إرسال', sending: 'جارٍ الإرسال...', success: 'تم إرسال الطلب!', btn: 'طلب قائمة الأسعار' },
};

const Hero = () => {
  const { t, language } = useLanguage();
  const { isEditMode } = useAdmin();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPriceForm, setShowPriceForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', city: '' });
  const [formSent, setFormSent] = useState(false);
  const navigate = useNavigate();
  const pf = priceFormTexts[language] || priceFormTexts.ru;

  const slides = [
    {
      title: {
        ru: 'Первый и специализированный завод по производству гипсовиниловых панелей в Казахстане',
        kz: 'Қазақстандағы гипсвинил панельдерін өндірудің алғашқы және мамандандырылған зауыты',
        en: 'The first and specialized plant for production of gypsum-vinyl panels in Kazakhstan', ar: 'أول مصنع متخصص في إنتاج الألواح الجبسية الفينيلية في كازاخستان'
      },
      image: '/images/hero/hero_3.jpg'
    },
    {
      title: {
        ru: 'Стеновые панели GIPSOVINIL.KZ — база для современного эстетичного интерьера',
        kz: 'GIPSOVINIL.KZ қабырға панельдері — заманауи эстетикалық интерьердің негізі',
        en: 'GIPSOVINIL.KZ wall panels — foundation for modern aesthetic interior', ar: 'ألواح الجدران GIPSOVINIL.KZ — الأساس لتصميم داخلي جمالي عصري'
      },
      image: '/images/hero/hero_4.jpg'
    },
    {
      title: {
        ru: 'Решения для офисов, клиник, отелей и общественных пространств',
        kz: 'Кеңселер, клиникалар, қонақ үйлер және қоғамдық кеңістіктерге арналған шешімдер',
        en: 'Solutions for offices, clinics, hotels and public spaces', ar: 'حلول للمكاتب والعيادات والفنادق والفضاءات العامة'
      },
      image: '/images/hero/hero_5.jpg'
    }
  ];

  const handlePriceSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name, phone: formData.phone, city: formData.city, comment: 'Запрос прайс-листа' }),
      });
      if (res.ok) {
        setFormSent(true);
        setTimeout(() => { setShowPriceForm(false); setFormSent(false); setFormData({ name: '', phone: '', city: '' }); }, 2500);
      }
    } catch {}
  };

  const goToSlide = useCallback((index) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 800);
  }, [isTransitioning]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % slides.length);
  }, [currentSlide, slides.length, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  }, [currentSlide, slides.length, goToSlide]);

  useEffect(() => {
    if (isEditMode) return;
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide, isEditMode]);

  const slide = slides[currentSlide];

  return (
    <section id="home" className="relative h-screen overflow-hidden" data-testid="hero-section">
      {/* Background Images */}
      {slides.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-all duration-[1200ms] ease-in-out ${
            i === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
          }`}
        >
          <img
            src={s.image}
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
        </div>
      ))}

      {/* Subtle darkening at bottom for label readability — no white wash on the image. */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

      {/* Content — full width bottom bar */}
      <div className="absolute inset-0 flex items-end z-10">
        <div className="w-full">
          <div className="bg-[#061d36] px-6 md:px-16 lg:px-24 py-6" data-testid="hero-title">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold text-white leading-relaxed tracking-tight">
                {slide.title[language]}
              </h1>
              <button
                onClick={() => setShowPriceForm(true)}
                className="group inline-flex items-center gap-3 px-6 py-3 bg-[#8b4444] hover:bg-[#7a3838] text-white rounded-lg font-medium text-sm tracking-wide transition-all duration-300 whitespace-nowrap flex-shrink-0"
                data-testid="hero-cta-btn"
              >
                <span>{pf.btn}</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Price List Request Modal */}
      {showPriceForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" onClick={() => setShowPriceForm(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()} data-testid="price-form-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-[#061d36]">{pf.title}</h3>
              <button onClick={() => setShowPriceForm(false)} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-[#061d36]/50" />
              </button>
            </div>
            {formSent ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Send className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-lg font-semibold text-[#061d36]">{pf.success}</p>
              </div>
            ) : (
              <form onSubmit={handlePriceSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-[#061d36]/60 mb-1">{pf.name} *</label>
                  <input required value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))}
                    className="w-full px-4 py-3 border border-[#061d36]/15 rounded-xl text-sm focus:outline-none focus:border-[#8b4444] transition-colors"
                    data-testid="price-form-name" />
                </div>
                <div>
                  <label className="block text-sm text-[#061d36]/60 mb-1">{pf.phone} *</label>
                  <input required type="tel" value={formData.phone}
                    onChange={e => {
                      let v = e.target.value.replace(/\D/g, '');
                      if (v.startsWith('8')) v = '7' + v.slice(1);
                      if (!v.startsWith('7') && v.length > 0) v = '7' + v;
                      if (v.length > 11) v = v.slice(0, 11);
                      let formatted = '';
                      if (v.length > 0) formatted = '+' + v[0];
                      if (v.length > 1) formatted += ' (' + v.slice(1, 4);
                      if (v.length > 4) formatted += ') ' + v.slice(4, 7);
                      if (v.length > 7) formatted += '-' + v.slice(7, 9);
                      if (v.length > 9) formatted += '-' + v.slice(9, 11);
                      if (v.length >= 2 && v.length <= 4) formatted += ')';
                      setFormData(p => ({...p, phone: formatted}));
                    }}
                    placeholder="+7 (___) ___-__-__"
                    pattern="\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}"
                    title="+7 (XXX) XXX-XX-XX"
                    className="w-full px-4 py-3 border border-[#061d36]/15 rounded-xl text-sm focus:outline-none focus:border-[#8b4444] transition-colors"
                    data-testid="price-form-phone" />
                </div>
                <div>
                  <label className="block text-sm text-[#061d36]/60 mb-1">{pf.city} *</label>
                  <input required value={formData.city} onChange={e => setFormData(p => ({...p, city: e.target.value}))}
                    className="w-full px-4 py-3 border border-[#061d36]/15 rounded-xl text-sm focus:outline-none focus:border-[#8b4444] transition-colors"
                    data-testid="price-form-city" />
                </div>
                <button type="submit"
                  className="w-full py-3.5 bg-[#8b4444] hover:bg-[#7a3838] text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2"
                  data-testid="price-form-submit">
                  <Send className="w-4 h-4" />
                  {pf.send}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Navigation arrows — thin, minimal */}
      <button
        onClick={prevSlide}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
        data-testid="hero-prev-btn"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6" strokeWidth={1.5} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center text-white/50 hover:text-white transition-colors"
        data-testid="hero-next-btn"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6" strokeWidth={1.5} />
      </button>

      {/* Slide counter — top-right, subtle */}
      <div className="absolute top-24 right-6 md:right-16 z-20 text-white/40 text-xs tracking-[0.3em] font-light">
        {String(currentSlide + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
      </div>
    </section>
  );
};

export default Hero;
